import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Filters that live in the URL, so a filtered view can be linked, shared,
 * bookmarked, and opened from a count elsewhere ("12 blocked" → the list).
 *
 * `defaults` declares every key and its default; values equal to their
 * default are omitted from the URL to keep links short. Arrays are
 * comma-separated. Booleans are "1" / absent.
 *
 *   const [f, setF] = useUrlState({ q: '', status: [] as string[], page: 1, owed: false });
 *   setF({ q: 'abc', page: 1 });
 */
type Value = string | number | boolean | string[];

export function parseParam<T extends Value>(raw: string | null, fallback: T): T {
  if (raw === null) return fallback;
  if (Array.isArray(fallback)) return (raw === '' ? [] : raw.split(',')) as T;
  if (typeof fallback === 'number') {
    const n = Number(raw);
    return (Number.isFinite(n) ? n : fallback) as T;
  }
  if (typeof fallback === 'boolean') return (raw === '1' || raw === 'true') as T;
  return raw as T;
}

export function serializeParam(value: Value): string | null {
  if (Array.isArray(value)) return value.join(',');
  if (typeof value === 'boolean') return value ? '1' : null;
  return String(value);
}

function sameValue(a: Value, b: Value): boolean {
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((x, i) => x === b[i]);
  return a === b;
}

export function useUrlState<T extends Record<string, Value>>(
  defaults: T,
): [T, (patch: Partial<T>) => void] {
  const [params, setParams] = useSearchParams();
  // `defaults` is usually an inline literal; key on its JSON so a new object
  // each render doesn't churn the memo.
  const defaultsKey = JSON.stringify(defaults);
  const state = useMemo(() => {
    const out = {} as T;
    for (const key of Object.keys(defaults) as (keyof T)[]) {
      out[key] = parseParam(params.get(String(key)), defaults[key]) as T[keyof T];
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, defaultsKey]);

  const update = useCallback(
    (patch: Partial<T>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch) as [string, Value][]) {
            const def = defaults[key] as Value;
            const s = value === undefined ? null : serializeParam(value);
            if (s === null || (def !== undefined && sameValue(value, def))) next.delete(key);
            else next.set(key, s);
          }
          return next;
        },
        { replace: true },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setParams, defaultsKey],
  );

  return [state, update];
}
